import { Link } from "react-router-dom";
import VaultLogo from "@/components/VaultLogo";

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <header className="border-b-[1.5px] border-[var(--brand-ink)] grid-paper">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="back-home">
            <VaultLogo size={24}/>
            <span className="font-display text-2xl font-semibold tracking-tight">JobVault</span>
          </Link>
          <Link to="/" className="font-mono-d text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">/back</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-tiny">/privacy policy</p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-semibold mt-2 mb-2">
          your data, your rules.
        </h1>
        <p className="text-sm text-[var(--brand-muted)] font-mono-d mb-10">
          last updated: february 2026 · governed by UK GDPR &amp; PECR
        </p>

        <section className="card-hard rounded-md p-6 bg-white mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">the short version</h2>
          <ul className="text-sm space-y-2 list-disc pl-5 leading-relaxed">
            <li>we only store what you type in. no tracking pixels, no ad cookies, no data brokers.</li>
            <li>your vault is accessible only to you, secured with a JWT cookie. we never sell it.</li>
            <li>the Chrome extension reads your vault from your account on your command — credentials never leave your browser.</li>
            <li>you can export everything as JSON / Markdown / .docx at any time.</li>
            <li>delete your account from Settings — wiped permanently within 14 days (grace period for accidental clicks).</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">1. who&apos;s the data controller</h2>
          <p className="text-sm leading-relaxed">
            <b>[JobVault Ltd / Sole Trader — REPLACE WITH LEGAL ENTITY]</b>, registered in England &amp; Wales (company number <b>[PLACEHOLDER]</b>), is the data controller for the personal data described in this policy.
            ICO registration number: <b>[PLACEHOLDER]</b>.
            Contact our Data Protection lead at <a className="underline" href="mailto:privacy@jobvault.co.uk">privacy@jobvault.co.uk</a>.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">2. what we collect &amp; lawful basis</h2>
          <div className="overflow-x-auto card-hard rounded-md bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[var(--brand-bg)] border-b-[1.5px] border-[var(--brand-ink)]">
                <tr>
                  <th className="text-left p-3 font-mono-d label-tiny">data</th>
                  <th className="text-left p-3 font-mono-d label-tiny">purpose</th>
                  <th className="text-left p-3 font-mono-d label-tiny">lawful basis (UK GDPR Art. 6)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Account info</b> (email, bcrypt password hash, name)</td>
                  <td className="p-3">Authenticate you and provide the service</td>
                  <td className="p-3">Contract (6(1)(b))</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Vault contents</b> (work history, education, refs, docs, applications)</td>
                  <td className="p-3">Store and display back to you, enable autofill</td>
                  <td className="p-3">Contract (6(1)(b))</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>AI analyses</b> (culture scores, cover letter critiques, tailored CVs)</td>
                  <td className="p-3">Generate the personalised guidance you asked for</td>
                  <td className="p-3">Contract (6(1)(b)) — disclosed on first use</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Special-category data</b> (UK diversity questionnaire: ethnicity, gender, disability, etc.) — only if you fill it in</td>
                  <td className="p-3">Help you complete the diversity sections employers ask for</td>
                  <td className="p-3">Explicit consent (9(2)(a)) — you opt in</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Payment info</b> (Stripe customer ID, subscription status)</td>
                  <td className="p-3">Bill Premium / Unlimited subscribers</td>
                  <td className="p-3">Contract (6(1)(b)) + Legal obligation (HMRC records)</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Transactional emails</b> (password reset, doc-expiry, deletion notice)</td>
                  <td className="p-3">Run the service safely</td>
                  <td className="p-3">Contract (6(1)(b))</td>
                </tr>
                <tr>
                  <td className="p-3"><b>Marketing emails</b> (only if you opt in)</td>
                  <td className="p-3">Tell you about new features</td>
                  <td className="p-3">Consent (6(1)(a)) — opt-in at signup, toggle in Settings</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm leading-relaxed mt-3">
            <b>What we do NOT collect:</b> tracking pixels, Google Analytics, Mixpanel, Hotjar, Facebook/TikTok pixels, browser fingerprints, ad-network cookies, or anything resembling targeted advertising.
          </p>
        </section>

        <section className="card-hard rounded-md p-6 bg-[var(--pastel-mint)] mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">3. the chrome extension</h2>
          <p className="text-sm leading-relaxed mb-2">
            The JobVault Chrome extension is a thin client that reads your vault from <span className="font-mono-d">jobvault.co.uk</span> and uses it to autofill job application forms on the active tab.
          </p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li><b>What it accesses:</b> the URL and form fields of the active tab when you press &ldquo;autofill this page&rdquo; or &ldquo;scan this job ad&rdquo;. Nothing is read in the background.</li>
            <li><b>What it sends to us:</b> when you press &ldquo;scan this job ad&rdquo;, the visible text of that page is POSTed to <span className="font-mono-d">/api/culture/analyze</span>. The raw page text is not retained — only the resulting score and red-flag list.</li>
            <li><b>What it sends elsewhere:</b> nothing. The extension makes requests only to <span className="font-mono-d">jobvault.co.uk</span> — never to third parties.</li>
            <li><b>Local storage:</b> the extension caches your vault JSON in <span className="font-mono-d">chrome.storage.local</span>. Cleared when you click &ldquo;disconnect&rdquo; in the popup.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">4. sub-processors</h2>
          <p className="text-sm leading-relaxed mb-3">
            We use these trusted providers to run JobVault. We&apos;ve signed data-processing agreements with each.
          </p>
          <div className="overflow-x-auto card-hard rounded-md bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[var(--brand-bg)] border-b-[1.5px] border-[var(--brand-ink)]">
                <tr>
                  <th className="text-left p-3 font-mono-d label-tiny">provider</th>
                  <th className="text-left p-3 font-mono-d label-tiny">role</th>
                  <th className="text-left p-3 font-mono-d label-tiny">data location</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Anthropic (Claude)</b></td>
                  <td className="p-3">Powers Vault Coach (CV Tailor, Cover Letter Checker, Interview Prep) + Culture Barometer. Doesn&apos;t train on API data.</td>
                  <td className="p-3">US (DPA + SCCs)</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Stripe</b></td>
                  <td className="p-3">Processes subscription payments. We never see your card number.</td>
                  <td className="p-3">EU/UK</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Resend</b></td>
                  <td className="p-3">Delivers password resets, doc-expiry reminders, monthly check-ins.</td>
                  <td className="p-3">EU</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>MongoDB Atlas</b></td>
                  <td className="p-3">Hosts the vault database. Encrypted at rest (AES-256).</td>
                  <td className="p-3">EU (Frankfurt region)</td>
                </tr>
                <tr className="border-b border-[var(--brand-bg)]">
                  <td className="p-3"><b>Cloudflare</b></td>
                  <td className="p-3">CDN + DDoS protection + WAF on jobvault.co.uk.</td>
                  <td className="p-3">Global edge (UK PoPs prioritised)</td>
                </tr>
                <tr>
                  <td className="p-3"><b>Emergent</b></td>
                  <td className="p-3">Application hosting + secure object storage for uploaded documents.</td>
                  <td className="p-3">EU/UK</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">5. data retention</h2>
          <ul className="text-sm space-y-2 list-disc pl-5 leading-relaxed">
            <li><b>Active accounts:</b> we keep your data for as long as your account is open.</li>
            <li><b>Account deletion:</b> when you delete your account, we mark it for permanent erasure. A 14-day grace window lets you change your mind by logging back in. After that everything is wiped within 30 days.</li>
            <li><b>Payment records:</b> Stripe retains payment history for 7 years for HMRC / tax compliance. Even after account deletion, we&apos;re legally required to keep transaction IDs for that period.</li>
            <li><b>Backups:</b> encrypted database backups roll over every 30 days. Deleted data persists in backups for up to 30 days before being overwritten.</li>
            <li><b>AI provider logs:</b> Anthropic retains API call metadata for up to 30 days for abuse monitoring, then deletes.</li>
            <li><b>Email logs:</b> Resend retains delivery logs for 30 days.</li>
            <li><b>Inactive accounts:</b> if an account hasn&apos;t logged in for 24 months, we&apos;ll email you a heads-up before scheduling deletion.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">6. your UK GDPR rights</h2>
          <p className="text-sm leading-relaxed mb-2">You have the right to:</p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li><b>Access</b> all your data — Settings → Export, or call <span className="font-mono-d">GET /api/export/profile</span>.</li>
            <li><b>Rectify</b> any field directly in the app.</li>
            <li><b>Delete</b> your account from Settings (right to erasure).</li>
            <li><b>Portability</b> — export as JSON, Markdown, .docx, or LinkedIn-ready plain text.</li>
            <li><b>Restrict / object</b> to processing — email <a className="underline" href="mailto:privacy@jobvault.co.uk">privacy@jobvault.co.uk</a>.</li>
            <li><b>Withdraw consent</b> at any time (marketing toggle in Settings, AI consent reset, special-category withdrawal).</li>
            <li><b>Not be subject to automated decision-making</b> — our AI features are advisory; we don&apos;t make consequential decisions about you without human review.</li>
            <li><b>Complain to the UK ICO</b> if you think we&apos;ve mishandled your data: <a className="underline" href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ico.org.uk/make-a-complaint</a>.</li>
          </ul>
          <p className="text-sm leading-relaxed mt-3">
            We respond to data requests within <b>30 days</b> of receiving them.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">7. how we keep it safe</h2>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li>Passwords are bcrypt-hashed (cost factor 12). We can&apos;t see them.</li>
            <li>All traffic is HTTPS-only with HSTS.</li>
            <li>Auth cookies are HttpOnly, Secure, SameSite=None.</li>
            <li>Database encrypted at rest (MongoDB Atlas AES-256).</li>
            <li>Rate-limiting on login + register to defeat credential stuffing.</li>
            <li>Documents stored in encrypted object storage with signed-URL access only.</li>
          </ul>
          <p className="text-sm leading-relaxed mt-3">
            If we suffer a personal data breach that&apos;s likely to risk your rights, we&apos;ll notify the ICO within 72 hours and email you without undue delay.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">8. international transfers</h2>
          <p className="text-sm leading-relaxed">
            Most of your data stays in the UK / EU. When we send data to Anthropic (US), we rely on the UK Addendum to the EU Standard Contractual Clauses for the transfer.
            All other sub-processors host your data in the UK or EU.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">9. children</h2>
          <p className="text-sm leading-relaxed">
            JobVault is for users aged 16+. We don&apos;t knowingly collect data from under-16s. If we discover an account belongs to someone under 16, we&apos;ll delete it.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">10. changes to this policy</h2>
          <p className="text-sm leading-relaxed">
            If we make material changes (e.g. new sub-processor, new data category), we&apos;ll email you at least 14 days before the change takes effect. The &ldquo;last updated&rdquo; date at the top always reflects the most recent change.
          </p>
        </section>

        <section className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-3">11. contact</h2>
          <p className="text-sm leading-relaxed">
            Privacy questions, data requests, or just want to chat — <a className="underline font-medium" href="mailto:privacy@jobvault.co.uk">privacy@jobvault.co.uk</a>.
            We reply within 5 working days; data requests within 30 days.
          </p>
        </section>

        <p className="text-xs font-mono-d text-[var(--brand-muted)] mb-10">
          See also our <Link to="/terms" className="underline">Terms of Service</Link> and <Link to="/cookies" className="underline">Cookie Policy</Link>.
        </p>
      </main>

      <footer className="border-t-[1.5px] border-[var(--brand-ink)] py-6 text-center font-mono-d text-xs text-[var(--brand-muted)]">
        © JobVault {new Date().getFullYear()} · made with love &amp; rage in the UK
      </footer>
    </div>
  );
}
